import { useMemo, useState } from "react";
import type { Auction, AuctionBid } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { auctionService } from "@/services";
import { Button } from "@/components/base/Button";
import { computeLivePreview } from "@/utils/auction";
import { HeaderBand } from "./HeaderBand";
import { BidEntry } from "./BidEntry";
import { BidTable } from "./BidTable";
import { LiveCalculationPanel } from "./LiveCalculationPanel";
import { EligibilityPanel } from "./EligibilityPanel";
import { CloseAuctionModal } from "./CloseAuctionModal";

interface LiveAuctionConsoleProps {
  auction: Auction;
  onClosed: () => void;
}

/**
 * The live auction workspace. Bids are tracked locally as they are called out,
 * with the current lowest bid driving the persistent calculation panel. Only the
 * close action sends the winning bid to the API, which computes the authoritative
 * result and returns the closed record.
 */
export function LiveAuctionConsole({ auction, onClosed }: LiveAuctionConsoleProps) {
  const { user, isViewer } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";

  const [bids, setBids] = useState<AuctionBid[]>(auction.bids);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  const standing = useMemo(() => bids.filter((b) => b.status === "standing"), [bids]);
  const lowest = useMemo(
    () => (standing.length ? [...standing].sort((a, b) => a.amount - b.amount)[0] : null),
    [standing]
  );
  const currentLowest = lowest?.amount ?? null;

  const preview = useMemo(
    () =>
      computeLivePreview({
        pool: auction.pool,
        monthlyInstallment: auction.monthlyInstallment,
        memberCount: auction.memberCount,
        dividendTiming: auction.dividendTiming,
        amount: currentLowest ?? auction.pool,
        commissionType: auction.commissionType,
        commissionValue: auction.commissionValue,
      }),
    [auction, currentLowest]
  );

  const handleRecord = async (memberId: string, amount: number) => {
    const eligibleMember = auction.eligible.find((m) => m.memberId === memberId);
    try {
      const bid = await auctionService.placeBid(auction.id, { memberId, groupMemberId: eligibleMember?.groupMemberId }, amount);
      setBids((prev) => [...prev, bid]);
      toast.success("Bid recorded", `Bid of ${amount.toLocaleString("en-IN")} recorded.`);
    } catch (err) {
      toast.error("Could not record bid", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const handleWithdraw = async (bid: AuctionBid) => {
    const ok = await confirm({
      title: "Withdraw this bid?",
      message: `Withdraw the ${bid.amount.toLocaleString("en-IN")} bid. It will remain in the history but no longer count.`,
      confirmLabel: "Withdraw bid",
      destructive: true,
    });
    if (!ok) return;
    try {
      await auctionService.withdrawBid(bid.id);
      setBids((prev) => prev.map((b) => (b.id === bid.id ? { ...b, status: "withdrawn" } : b)));
      toast.info("Bid withdrawn", "The bid no longer counts towards the standing bid.");
    } catch (err) {
      toast.error("Could not withdraw bid", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const handleConfirmClose = async () => {
    if (!lowest) return;
    setClosing(true);
    try {
      await auctionService.closeAuction(auction.groupId, auction.cycle, lowest.amount, bids, actor);
      toast.success("Auction closed", `${auction.groupName} cycle ${auction.cycle} is now locked in.`);
      setCloseOpen(false);
      onClosed();
    } catch (err) {
      toast.error("Could not close auction", err instanceof Error ? err.message : "Please try again.");
      setClosing(false);
    }
  };

  return (
    <div>
      <HeaderBand
        auction={auction}
        pool={auction.pool}
        currentLowest={currentLowest}
        currentInstallment={preview.netInstallment}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left — bid entry + live bid table */}
        <div className="space-y-4 lg:col-span-2">
          <BidEntry auction={auction} currentLowest={currentLowest} onRecord={handleRecord} />

          <BidTable auction={auction} bids={bids} onWithdraw={handleWithdraw} isViewer={isViewer} />

          <div className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="text-sm text-foreground-600">
              {currentLowest != null ? (
                <>
                  Current lowest bid:{" "}
                  <span className="tnum font-semibold text-accent-900">{currentLowest.toLocaleString("en-IN")}</span>
                </>
              ) : (
                "No bids yet — record the first bid to enable closing."
              )}
            </div>
            <Button
              variant="danger"
              icon={<i className="ri-lock-line" />}
              disabled={currentLowest == null || isViewer}
              onClick={() => setCloseOpen(true)}
            >
              Close auction
            </Button>
          </div>
        </div>

        {/* Right — live calculation + eligibility */}
        <div className="space-y-4">
          <LiveCalculationPanel auction={auction} preview={preview} />
          <EligibilityPanel auction={auction} />
        </div>
      </div>

      <CloseAuctionModal
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        auction={auction}
        winnerBid={lowest}
        onConfirm={handleConfirmClose}
        closing={closing}
        actor={actor}
      />
    </div>
  );
}